/**
 * Delivery Time Utilities
 * Shared utility functions for time calculations and formatting
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST_TIMEZONE = "Asia/Kolkata";
const DAYS_FORMAT_THRESHOLD = 6;

// Default Quick ETA options in minutes
export const DEFAULT_ETA_MINS = [20, 25, 45, 60, 120, 150, 200, 220, 250, 300];

/**
 * Convert minutes to readable format
 * @example minutesToHours(90) => "1:30 hrs"
 * @example minutesToHours(60) => "1 hrs"
 * @example minutesToHours(30) => "30 mins"
 */
export function minutesToHours(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours === 0) return `${remainingMinutes} mins`;
  if (remainingMinutes > 0) return `${hours}:${remainingMinutes} hrs`;
  return `${hours} hrs`;
}

/**
 * Calculate future date for datetime-local input
 * Returns IST datetime string formatted for datetime-local input (YYYY-MM-DDTHH:mm)
 */
export function calculateFutureDate(minutes: number): string {
  const futureIST = dayjs().tz(IST_TIMEZONE).add(minutes, "minutes");
  return futureIST.format("YYYY-MM-DDTHH:mm");
}

// /**
//  * Format CPT for display
//  * @example formatCPTDisplay("2024-01-15T14:30:00Z") => "15 JAN, 2:30 PM"
//  */
// export function formatCPTDisplay(isoString: string): string {
//   const date = new Date(isoString);
//   const day = date.getDate();
//   const month = date.toLocaleDateString("en-IN", { month: "short" });
//   const time = date.toLocaleTimeString("en-IN", {
//     hour: "numeric",
//     minute: "2-digit",
//     hour12: true,
//   });
//   return `${day} ${month}, ${time}`.toUpperCase();
// }

/**
 * Parse quick ETA minutes from config string
 * Falls back to DEFAULT_ETA_MINS if parsing fails
 */
export function parseQuickEtaMins(quickEtasMins?: string): number[] {
  if (!quickEtasMins) return DEFAULT_ETA_MINS;
  const parsed = quickEtasMins
    .split(",")
    .map((m) => parseInt(m.trim()))
    .filter((m) => !isNaN(m));
  return parsed.length > 0 ? parsed : DEFAULT_ETA_MINS;
}

function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";

  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function formatOrdinalDateTime(date: dayjs.Dayjs): string {
  return `${date.date()}${getOrdinalSuffix(date.date())} ${date.format(
    "MMM",
  )}, ${date.format("HH:mm")}`;
}

/**
 * Format date/time as "Today, 7:15 PM" or "Tomorrow, 7:15 PM" or "17th Jul, 18:30"
 */
export function formatSmartDateTime(isoString: string): string {
  if (!isoString) return "";

  const date = dayjs(isoString).tz(IST_TIMEZONE);
  if (!date.isValid()) return "";

  const today = dayjs().tz(IST_TIMEZONE).startOf("day");
  const tomorrow = today.add(1, "day");
  const inputDate = date.startOf("day");

  const timeStr = date.format("h:mm A");

  if (inputDate.isSame(today)) {
    return `Today, ${timeStr}`;
  } else if (inputDate.isSame(tomorrow)) {
    return `Tomorrow, ${timeStr}`;
  } else {
    return formatOrdinalDateTime(date);
  }
}

/**
 * Calculate days from today to a given date
 */
function daysFromToday(dateString: string): number {
  const date = dayjs(dateString).tz(IST_TIMEZONE);
  if (!date.isValid()) return 0;

  const today = dayjs().tz(IST_TIMEZONE).startOf("day");
  const targetDate = date.startOf("day");
  const diffDays = targetDate.diff(today, "day");
  return Math.max(0, diffDays);
}

/**
 * Format CPT range display
 * - If range is up to 6 days away: shows smart date/time format
 * - If range goes beyond 6 days: shows "X - Y Days" format
 */
export function formatCPTRange(cptMin: string, cptMax: string): string {
  if (!cptMin || !cptMax) return "";

  const minDays = daysFromToday(cptMin);
  const maxDays = daysFromToday(cptMax);

  if (minDays <= DAYS_FORMAT_THRESHOLD && maxDays <= DAYS_FORMAT_THRESHOLD) {
    if (cptMin === cptMax) return formatSmartDateTime(cptMax);
    return `${formatSmartDateTime(cptMin)} - ${formatSmartDateTime(cptMax)}`;
  }

  if (minDays === maxDays) {
    return `${minDays} Days`;
  }

  return `${minDays} - ${maxDays} Days`;
}
